import axios from "axios";

axios.defaults.withCredentials = true;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Heavy LLM/web-search endpoints can be dropped by the ingress proxy (~60s) even though
// the backend completes and persists. When a POST fails, poll the GET until the result lands.
async function pollUntil(fetchFn, isDone, { interval = 5000, timeout = 150000 } = {}) {
  const start = Date.now();
  while (Date.now() - start < timeout) {
    await new Promise((r) => setTimeout(r, interval));
    try { const v = await fetchFn(); if (isDone(v)) return v; } catch { /* keep polling */ }
  }
  throw new Error("timed out waiting for KURT");
}

export const api = {
  bootstrap: () => axios.get(`${API}/bootstrap`).then((r) => r.data),
  getBusiness: () => axios.get(`${API}/business`).then((r) => r.data),
  updateBusiness: (d) => axios.put(`${API}/business`, d).then((r) => r.data),
  getSettings: () => axios.get(`${API}/settings`).then((r) => r.data),
  updateSettings: (d) => axios.put(`${API}/settings`, d).then((r) => r.data),
  pause: () => axios.post(`${API}/pause`).then((r) => r.data),
  resume: () => axios.post(`${API}/resume`).then((r) => r.data),
  integrations: () => axios.get(`${API}/integrations`).then((r) => r.data),

  getToday: () => axios.get(`${API}/today`).then((r) => r.data),
  todayDigest: () => axios.get(`${API}/today/digest`).then((r) => r.data),

  source: async (brief) => {
    const before = await axios.get(`${API}/opportunities`).then((r) => r.data.length).catch(() => 0);
    try {
      return (await axios.post(`${API}/opportunities/source`, { brief })).data;
    } catch {
      return pollUntil(() => axios.get(`${API}/opportunities`).then((r) => r.data),
        (all) => all.length > before);
    }
  },
  getOpps: () => axios.get(`${API}/opportunities`).then((r) => r.data),
  deleteOpp: (id) => axios.delete(`${API}/opportunities/${id}`).then((r) => r.data),
  clearOpps: () => axios.post(`${API}/opportunities/clear`).then((r) => r.data),
  deleteAction: (id) => axios.delete(`${API}/actions/${id}`).then((r) => r.data),
  clearActions: () => axios.post(`${API}/actions/clear`).then((r) => r.data),
  deleteConversation: (id) => axios.delete(`${API}/conversations/${id}`).then((r) => r.data),
  deleteLedger: (id) => axios.delete(`${API}/ledger/${id}`).then((r) => r.data),
  huntNodes: async (brief) => {
    const before = await axios.get(`${API}/opportunities`).then((r) => r.data.length).catch(() => 0);
    try {
      return (await axios.post(`${API}/opportunities/nodes`, { brief })).data;
    } catch {
      return pollUntil(() => axios.get(`${API}/opportunities`).then((r) => r.data),
        (all) => all.length > before);
    }
  },
  getClusters: () => axios.get(`${API}/clusters`).then((r) => r.data),
  mapsStatus: () => axios.get(`${API}/maps/status`).then((r) => r.data),
  mapsProspect: (query, location) => axios.post(`${API}/opportunities/maps`, { query, location }).then((r) => r.data),
  absorb: (url, channel) => axios.post(`${API}/absorb`, { url, channel }).then((r) => r.data),
  getPartnerCandidates: () => axios.get(`${API}/sales-partners/candidates`).then((r) => r.data),
  recruitPartners: async (brief) => {
    const before = await axios.get(`${API}/sales-partners/candidates`).then((r) => r.data.length).catch(() => 0);
    try {
      return (await axios.post(`${API}/sales-partners/recruit`, { brief })).data;
    } catch {
      return pollUntil(() => axios.get(`${API}/sales-partners/candidates`).then((r) => r.data), (all) => all.length > before);
    }
  },
  onboardPartner: (cid, name, email) => axios.post(`${API}/sales-partners/${cid}/onboard`, { name, email }).then((r) => r.data),
  deleteCandidate: (cid) => axios.delete(`${API}/sales-partners/${cid}`).then((r) => r.data),
  patchCandidate: (cid, patch) => axios.patch(`${API}/sales-partners/${cid}`, patch).then((r) => r.data),

  writeCopy: (instruction, context, tone) => axios.post(`${API}/copy/write`, { instruction, context, tone }).then((r) => r.data),
  getTemplates: (category) => axios.get(`${API}/templates${category ? `?category=${encodeURIComponent(category)}` : ""}`).then((r) => r.data),
  addTemplate: (t) => axios.post(`${API}/templates`, t).then((r) => r.data),
  updateTemplate: (id, t) => axios.put(`${API}/templates/${id}`, t).then((r) => r.data),
  deleteTemplate: (id) => axios.delete(`${API}/templates/${id}`).then((r) => r.data),
  editCoverCopy: (id, patch) => axios.put(`${API}/covers/${id}/copy`, patch).then((r) => r.data),
  autoSentLog: () => axios.get(`${API}/actions/auto-sent`).then((r) => r.data),
  rejectAction: (id) => axios.post(`${API}/actions/${id}/decision`, { decision: "reject" }).then((r) => r.data),
  getVoicePresets: () => axios.get(`${API}/voice-presets`).then((r) => r.data),
  addVoicePreset: (name, tone) => axios.post(`${API}/voice-presets`, { name, tone }).then((r) => r.data),
  activateVoicePreset: (id) => axios.post(`${API}/voice-presets/${id}/activate`).then((r) => r.data),
  deleteVoicePreset: (id) => axios.delete(`${API}/voice-presets/${id}`).then((r) => r.data),
  dailyRecap: () => axios.get(`${API}/recap/daily`).then((r) => r.data),

  seoStatus: () => axios.get(`${API}/seo/status`).then((r) => r.data),
  seoFunnel: () => axios.get(`${API}/seo/funnel`).then((r) => r.data),
  seoKeywords: () => axios.get(`${API}/seo/keywords`).then((r) => r.data),
  seoDiscover: async (brief) => {
    const before = await axios.get(`${API}/seo/keywords`).then((r) => r.data.length).catch(() => 0);
    try { return (await axios.post(`${API}/seo/discover`, { brief })).data; }
    catch { return pollUntil(() => axios.get(`${API}/seo/keywords`).then((r) => r.data), (all) => all.length > before); }
  },
  seoArticles: () => axios.get(`${API}/seo/articles`).then((r) => r.data),
  seoBatchWrite: (keywords) => axios.post(`${API}/seo/batch-write`, { keywords }).then((r) => r.data),
  seoRepurpose: (id) => axios.post(`${API}/seo/articles/${id}/repurpose`).then((r) => r.data),
  seoGrowth: (target) => axios.get(`${API}/seo/growth?target=${target || 100000}`).then((r) => r.data),
  seoWrite: async (keyword, title) => {
    const before = await axios.get(`${API}/seo/articles`).then((r) => r.data.length).catch(() => 0);
    try { return (await axios.post(`${API}/seo/write`, { keyword, title })).data; }
    catch { return pollUntil(() => axios.get(`${API}/seo/articles`).then((r) => r.data), (all) => all.length > before).then((all) => all[0]); }
  },
  seoEditArticle: (id, patch) => axios.put(`${API}/seo/articles/${id}`, patch).then((r) => r.data),
  seoPublish: (id) => axios.post(`${API}/seo/articles/${id}/publish`).then((r) => r.data),
  seoDeleteArticle: (id) => axios.delete(`${API}/seo/articles/${id}`).then((r) => r.data),
  nlSubscribe: (email, source_article_id) => axios.post(`${API}/seo/newsletter/subscribe`, { email, source_article_id }).then((r) => r.data),
  nlSubscribers: () => axios.get(`${API}/seo/newsletter/subscribers`).then((r) => r.data),
  nlDeleteSubscriber: (id) => axios.delete(`${API}/seo/newsletter/subscribers/${id}`).then((r) => r.data),
  nlDraft: () => axios.post(`${API}/seo/newsletter/draft`).then((r) => r.data),
  nlIssues: () => axios.get(`${API}/seo/newsletter/issues`).then((r) => r.data),
  nlEditIssue: (id, patch) => axios.put(`${API}/seo/newsletter/issues/${id}`, patch).then((r) => r.data),
  nlSendIssue: (id) => axios.post(`${API}/seo/newsletter/issues/${id}/send`).then((r) => r.data),
  nlTestIssue: (id, email) => axios.post(`${API}/seo/newsletter/issues/${id}/test`, { email }).then((r) => r.data),
  nlDeleteIssue: (id) => axios.delete(`${API}/seo/newsletter/issues/${id}`).then((r) => r.data),
  nlSummary: () => axios.get(`${API}/seo/newsletter/summary`).then((r) => r.data),
  mapClusters: async () => {
    const startedAt = new Date().toISOString();
    try {
      return (await axios.post(`${API}/clusters/map`)).data;
    } catch {
      return pollUntil(() => axios.get(`${API}/clusters`).then((r) => r.data),
        (c) => c && c.mapped_at && c.mapped_at > startedAt);
    }
  },
  getTrends: () => axios.get(`${API}/trends`).then((r) => r.data),
  scanTrends: async (brief) => {
    const startedAt = new Date().toISOString();
    try {
      return (await axios.post(`${API}/trends/scan`, { brief })).data;
    } catch {
      return pollUntil(() => axios.get(`${API}/trends`).then((r) => r.data),
        (t) => t && t.scanned_at && t.scanned_at > startedAt);
    }
  },
  genStrategies: async (id) => {
    try {
      return (await axios.post(`${API}/opportunities/${id}/strategies`)).data;
    } catch {
      return pollUntil(() => axios.get(`${API}/opportunities`).then((r) => r.data.find((o) => o.id === id)),
        (o) => o && (o.strategies || []).length >= 4);
    }
  },
  createAssets: async (id, strategy_id) => {
    const before = await axios.get(`${API}/actions`).then((r) => r.data.filter((a) => a.opportunity_id === id).length).catch(() => 0);
    try {
      return (await axios.post(`${API}/opportunities/${id}/create`, { strategy_id })).data;
    } catch {
      const acts = await pollUntil(() => axios.get(`${API}/actions`).then((r) => r.data),
        (all) => all.filter((a) => a.opportunity_id === id).length > before);
      return acts.filter((a) => a.opportunity_id === id).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
    }
  },
  designJourney: async (id, strategy_id) => {
    try {
      return (await axios.post(`${API}/opportunities/${id}/journey`, { strategy_id })).data;
    } catch {
      return pollUntil(() => axios.get(`${API}/opportunities`).then((r) => r.data.find((o) => o.id === id)),
        (o) => o && o.journey && (o.journey.steps || []).length > 0);
    }
  },

  getActions: () => axios.get(`${API}/actions`).then((r) => r.data),
  decide: (id, decision) => axios.post(`${API}/actions/${id}/decision`, { decision }).then((r) => r.data),
  approveReady: () => axios.post(`${API}/actions/approve-ready`).then((r) => r.data),

  getConversations: () => axios.get(`${API}/conversations`).then((r) => r.data),
  convoReply: (id) => axios.post(`${API}/conversations/${id}/reply`).then((r) => r.data),
  takeover: (id) => axios.post(`${API}/conversations/${id}/takeover`).then((r) => r.data),
  handback: (id) => axios.post(`${API}/conversations/${id}/handback`).then((r) => r.data),
  convoMessage: (id, text) => axios.post(`${API}/conversations/${id}/message`, { text }).then((r) => r.data),

  getSales: () => axios.get(`${API}/sales`).then((r) => r.data),
  runReview: () => axios.post(`${API}/review`).then((r) => r.data),
  setOutcome: (id, result, revenue) => axios.post(`${API}/ledger/${id}/outcome`, { result, revenue }).then((r) => r.data),

  getReferrers: () => axios.get(`${API}/referrers`).then((r) => r.data),  addReferrer: (name, type, email) => axios.post(`${API}/referrers`, { name, type, email }).then((r) => r.data),
  toggleReferrer: (code) => axios.post(`${API}/referrers/${code}/toggle`).then((r) => r.data),
  deleteReferrer: (id) => axios.delete(`${API}/referrers/${id}`).then((r) => r.data),
  addReferral: (code, referred_name, referred_email) => axios.post(`${API}/referrers/${code}/refer`, { referred_name, referred_email }).then((r) => r.data),
  toggleReferral: (id) => axios.post(`${API}/referrals/${id}/toggle`).then((r) => r.data),
  referralSummary: () => axios.get(`${API}/referrals/summary`).then((r) => r.data),

  listCovers: () => axios.get(`${API}/covers`).then((r) => r.data),
  coverMetrics: () => axios.get(`${API}/covers/metrics`).then((r) => r.data),
  createCover: (formData) => axios.post(`${API}/covers/create`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  shareCover: (id) => axios.post(`${API}/covers/${id}/share`).then((r) => r.data),
  nominateCover: (id, names) => axios.post(`${API}/covers/${id}/nominate`, { names }).then((r) => r.data),
  inviteCover: (id) => axios.post(`${API}/covers/${id}/invite`).then((r) => r.data),
  joinCover: (id) => axios.post(`${API}/covers/${id}/join`).then((r) => r.data),
  deleteCover: (id) => axios.delete(`${API}/covers/${id}`).then((r) => r.data),
  auditLog: () => axios.get(`${API}/audit-log`).then((r) => r.data),
  getSuppression: () => axios.get(`${API}/suppression`).then((r) => r.data),
  addSuppression: (email) => axios.post(`${API}/suppression`, { email }).then((r) => r.data),
  removeSuppression: (email) => axios.delete(`${API}/suppression/${encodeURIComponent(email)}`).then((r) => r.data),

  getChatHistory: (sid) => axios.get(`${API}/chat/history?session_id=${encodeURIComponent(sid)}`).then((r) => r.data),
  clearChat: (sid) => axios.delete(`${API}/chat/history?session_id=${encodeURIComponent(sid)}`).then((r) => r.data),
  act: (session_id, message) => axios.post(`${API}/chat/act`, { session_id, message }).then((r) => r.data),

  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }).then((r) => r.data),
  logout: () => axios.post(`${API}/auth/logout`).then((r) => r.data),
  me: () => axios.get(`${API}/auth/me`).then((r) => r.data),

  referralScheme: () => axios.get(`${API}/referral-scheme`).then((r) => r.data),
  updateReferralScheme: (patch) => axios.put(`${API}/referral-scheme`, patch).then((r) => r.data),
  compliancePolicy: () => axios.get(`${API}/compliance-policy`).then((r) => r.data),
  updateCompliancePolicy: (patch) => axios.put(`${API}/compliance-policy`, patch).then((r) => r.data),
  retentionReview: () => axios.get(`${API}/compliance/retention-review`).then((r) => r.data),
  retentionPurge: () => axios.post(`${API}/compliance/retention-purge`).then((r) => r.data),
  verifyEntity: (oppId) => axios.post(`${API}/opportunities/${oppId}/verify-entity`).then((r) => r.data),
  enrichOpp: (oppId) => axios.post(`${API}/opportunities/${oppId}/enrich`).then((r) => r.data),

  trafficScan: (focus) => axios.post(`${API}/traffic/scan`, { focus: focus || "" }).then((r) => r.data),
  trafficOpps: () => axios.get(`${API}/traffic/opportunities`).then((r) => r.data),
  trafficDelete: (id) => axios.delete(`${API}/traffic/opportunities/${id}`).then((r) => r.data),
  trafficClear: () => axios.post(`${API}/traffic/opportunities/clear`).then((r) => r.data),
  trafficToArticle: (id) => axios.post(`${API}/traffic/opportunities/${id}/to-article`).then((r) => r.data),
  discoveryDraft: (service, location) => axios.post(`${API}/traffic/discovery`, { service, location }).then((r) => r.data),
  discoveryList: () => axios.get(`${API}/traffic/discovery`).then((r) => r.data),
  discoveryDelete: (id) => axios.delete(`${API}/traffic/discovery/${id}`).then((r) => r.data),
  trafficAnalyze: () => axios.post(`${API}/traffic/analyze`).then((r) => r.data),
  trafficAnalysis: () => axios.get(`${API}/traffic/analysis`).then((r) => r.data),
  magazineSync: () => axios.post(`${API}/magazine/sync`).then((r) => r.data),
  magazineMembers: () => axios.get(`${API}/magazine/members`).then((r) => r.data),
  socialPlan: (source, article_id) => axios.post(`${API}/social/plan`, { source, article_id }).then((r) => r.data),
  socialPlans: () => axios.get(`${API}/social/plans`).then((r) => r.data),
  socialUpdate: (id, patch) => axios.patch(`${API}/social/plans/${id}`, patch).then((r) => r.data),
  socialDelete: (id) => axios.delete(`${API}/social/plans/${id}`).then((r) => r.data),
  socialSend: (id) => axios.post(`${API}/social/plans/${id}/send`).then((r) => r.data),
  studioConfig: () => axios.get(`${API}/social/studio-config`).then((r) => r.data),
  updateStudioConfig: (patch) => axios.put(`${API}/social/studio-config`, patch).then((r) => r.data),
  mapsJob: (jid) => axios.get(`${API}/maps/job/${jid}`).then((r) => r.data),

  inbox: () => axios.get(`${API}/inbox`).then((r) => r.data),
  inboxNudges: () => axios.get(`${API}/inbox/nudges`).then((r) => r.data),
  draftFollowup: (payload) => axios.post(`${API}/inbox/draft-followup`, payload).then((r) => r.data),
  sendFollowup: (payload) => axios.post(`${API}/inbox/send-followup`, payload).then((r) => r.data),

  outboundStatus: () => axios.get(`${API}/outbound/status`).then((r) => r.data),
  lockSending: () => axios.post(`${API}/outbound/lock`).then((r) => r.data),
  unlockSending: () => axios.post(`${API}/outbound/unlock`).then((r) => r.data),
};

export async function streamChat(sessionId, message, onDelta) {
  const res = await fetch(`${API}/chat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, message }),
  });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    onDelta(decoder.decode(value, { stream: true }));
  }
}
